import React from 'react';
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Copy, Trash2, Search, Plus } from 'lucide-react';
import { Switch } from '../../ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { TemplateText } from './chipsConfig';

const IconAction = ({ label, className, onClick, testId, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" className={`qc-icon-btn ${className}`} onClick={onClick} aria-label={label} data-testid={testId}>{children}</button>
    </TooltipTrigger>
    <TooltipContent side="top" className="bg-slate-900 text-white">{label}</TooltipContent>
  </Tooltip>
);

const ChipRow = ({ chip, index, Icon, reorderMode, onEdit, onDuplicate, onDelete, onToggle }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: chip.id, disabled: !reorderMode });
  const style = { transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null), transition, '--i': Math.min(index, 12) };

  return (
    <div ref={setNodeRef} style={style} className="qc-row qc-grid" data-testid={`chip-row-${chip.id}`} data-inactive={!chip.is_active} data-dragging={isDragging}>
      <span className="qc-row-index">{index + 1}</span>
      <button
        ref={setActivatorNodeRef}
        type="button"
        className="qc-drag"
        data-disabled={!reorderMode}
        aria-label={`Drag to reorder ${chip.label}`}
        data-testid={`chip-drag-${chip.id}`}
        {...attributes}
        {...listeners}
        tabIndex={reorderMode ? 0 : -1}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <div className="qc-row-label-wrap">
        <span className="qc-row-tile"><Icon className="w-4 h-4" /></span>
        <div className="min-w-0">
          <span className="qc-row-label" data-testid={`chip-label-${chip.id}`}>{chip.label}</span>
          {chip.automation === 'tax_collector_email' && <span className="qc-badge-auto" data-testid="chip-automation-badge">AUTO-EMAIL</span>}
          <div className="qc-row-tpl-inline"><TemplateText template={chip.template} /></div>
        </div>
      </div>

      <div className="qc-col-template min-w-0" data-testid={`chip-template-${chip.id}`}><TemplateText template={chip.template} /></div>

      <div className="qc-status">
        <Switch
          checked={!!chip.is_active}
          onCheckedChange={(v) => onToggle(chip, v)}
          disabled={reorderMode}
          className="qc-switch h-6 w-11 [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-5"
          aria-label={chip.is_active ? 'Deactivate chip' : 'Activate chip'}
          data-testid={`chip-toggle-${chip.id}`}
        />
        <span className="qc-status-text" data-active={!!chip.is_active} data-testid={`chip-status-${chip.id}`}>
          <span className="qc-status-dot" />{chip.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="qc-actions" style={{ visibility: reorderMode ? 'hidden' : 'visible' }}>
        <IconAction label="Edit chip" className="qc-icon-btn-edit" onClick={() => onEdit(chip)} testId={`chip-edit-${chip.id}`}><Pencil className="w-4 h-4" /></IconAction>
        <IconAction label="Duplicate — opens Add Chip pre-filled" className="qc-icon-btn-dup" onClick={() => onDuplicate(chip)} testId={`chip-duplicate-${chip.id}`}><Copy className="w-4 h-4" /></IconAction>
        <IconAction label="Delete permanently" className="qc-icon-btn-del" onClick={() => onDelete(chip)} testId={`chip-delete-${chip.id}`}><Trash2 className="w-4 h-4" /></IconAction>
      </div>
    </div>
  );
};

export const ChipTable = ({ rows, Icon, colors, reorderMode, searchQuery, onReorder, onAdd, ...rowHandlers }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = rows.map(r => r.id);
    onReorder(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };

  return (
    <div data-testid="chip-list" style={{ '--qc-accent': colors.accent, '--qc-tile': colors.tile, '--qc-soft': colors.soft, '--qc-text': colors.text }}>
      <div className="qc-thead qc-grid" role="row">
        <span>#</span><span /><span>Chip Label</span><span className="qc-col-template">Preview Template</span><span>Status</span><span>Actions</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rows.map(r => r.id)} strategy={verticalListSortingStrategy}>
          {rows.map((chip, idx) => (
            <ChipRow key={chip.id} chip={chip} index={idx} Icon={Icon} reorderMode={reorderMode} {...rowHandlers} />
          ))}
        </SortableContext>
      </DndContext>
      {rows.length === 0 && (
        <div className="qc-empty" data-testid="chips-empty-state">
          <div className="qc-empty-icon">{searchQuery ? <Search className="w-5 h-5" /> : <Plus className="w-5 h-5" />}</div>
          <p className="text-sm font-semibold text-slate-600">{searchQuery ? `No chips matching “${searchQuery}”` : 'No chips in this category yet'}</p>
          <p className="text-xs text-slate-400 mt-1">{searchQuery ? 'Try a different search term or clear the filter.' : 'Use “Add Chip” to create the first one.'}</p>
          {!searchQuery && <button type="button" className="qc-btn qc-btn-primary mt-4" onClick={onAdd} data-testid="chips-empty-add"><Plus className="w-4 h-4" />Add Chip</button>}
        </div>
      )}
    </div>
  );
};
